import '../../../scrollytelling.css'
import '../../../v2/cinematic.css'
import '../../usecases.css'
import './showcase.css'

import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { SceneProvider } from '../../../engine'
import { LANGS, StoryProvider, readLang, writeParam } from '../../i18n'
import { useShowStory } from './engine'
import { Field } from './field'

/**
 * Rahmen der Showcase-Versionen: dunkle Bühne, ein durchgehendes Partikelfeld
 * aus Aivy-Formen, farbiges Licht je Kapitel, Filmkorn und ein Lichtkegel am
 * Mauszeiger. Sections wie in Version 2, zusätzlich mit `glow` (zwei Farben)
 * und `particles` (Liste aus [l, spec]), wobei spec = (w, h, narrow) => Formation.
 */
export default function ShowStory({
  sections,
  copy,
  navExtra,
  theme = 'dark',
  className
}) {
  const [lang, setLang] = useState(readLang)

  const choose = (next) => {
    if (next === lang) return
    writeParam('lang', next === 'de' ? null : next)
    setLang(next)
  }

  return (
    <Story
      key={lang}
      sections={sections}
      copy={copy[lang]}
      lang={lang}
      onLang={choose}
      navExtra={navExtra}
      theme={theme}
      className={className}
    />
  )
}

const sectionShape = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    height: PropTypes.number,
    tone: PropTypes.string,
    flow: PropTypes.bool,
    glow: PropTypes.arrayOf(PropTypes.string),
    particles: PropTypes.array,
    Scene: PropTypes.elementType.isRequired
  })
)

ShowStory.propTypes = {
  sections: sectionShape.isRequired,
  copy: PropTypes.shape({ de: PropTypes.object, en: PropTypes.object })
    .isRequired,
  navExtra: PropTypes.node,
  theme: PropTypes.oneOf(['dark', 'light']),
  className: PropTypes.string
}

function Story({ sections, copy, lang, onLang, navExtra, theme, className }) {
  const canvasRef = useRef(null)
  const field = useRef(null)
  const rootRef = useRef(null)

  const onFrame = useCallback(
    ({ y, now, vh, layout, l }) => {
      const f = field.current
      if (!f) return
      const keys = []
      sections.forEach((s, sec) => {
        const box = layout[sec]
        if (!box || !s.particles) return
        s.particles.forEach(([at, spec], k) => {
          keys.push({
            y: box.top + at * Math.max(0, box.height - vh),
            spec,
            id: `${s.id}-${k}`,
            sec
          })
        })
      })
      const reduce = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
      f.render({ y, now, keys, ls: l, reduce })
    },
    [sections]
  )

  const story = useShowStory(sections.length, { onFrame })
  const { scrollToChapter } = story
  const current = sections[story.active]

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined
    const f = new Field(canvas, { theme })
    field.current = f
    let cancelled = false
    // Schrift-Formationen erst mit geladener Fira Sans sauber
    document.fonts?.ready.then(() => {
      if (!cancelled) f.cache.clear()
    })
    const resize = () => f.resize()
    const move = (e) => {
      f.point(e.clientX, e.clientY)
      rootRef.current?.style.setProperty('--mx', `${e.clientX}px`)
      rootRef.current?.style.setProperty('--my', `${e.clientY}px`)
    }
    const away = () => f.point(-9999, -9999)
    window.addEventListener('resize', resize)
    window.addEventListener('pointermove', move, { passive: true })
    document.addEventListener('pointerleave', away)
    return () => {
      cancelled = true
      field.current = null
      window.removeEventListener('resize', resize)
      window.removeEventListener('pointermove', move)
      document.removeEventListener('pointerleave', away)
    }
  }, [theme])

  const goTo = useCallback(
    (id) => {
      const index = sections.findIndex((s) => s.id === id)
      if (index >= 0) scrollToChapter(index)
    },
    [sections, scrollToChapter]
  )
  const value = useMemo(
    () => ({ lang, copy, goTo, theme }),
    [lang, copy, goTo, theme]
  )
  const glow = current.glow || ['#407BFF', '#FE79A7']

  return (
    <StoryProvider value={value}>
      <div
        ref={(el) => {
          rootRef.current = el
          story.storyRef.current = el
        }}
        lang={lang}
        className={clsx(
          'sty-root cin-root sc-root',
          theme === 'light' && 'sc-root--light',
          className
        )}
      >
        <div className='sc-glow' aria-hidden='true'>
          <i style={{ backgroundColor: glow[0] }} />
          <i style={{ backgroundColor: glow[1] }} />
        </div>
        <canvas ref={canvasRef} className='sc-canvas' aria-hidden='true' />
        <div className='sc-spot' aria-hidden='true' />
        <div className='sc-grain' aria-hidden='true' />

        <div ref={story.progressRef} className='sc-bar'>
          <div className='sc-bar__fill' />
          <nav className='sc-nav' aria-label={copy.ui.chapters}>
            <span className='sc-nav__dots'>
              {sections.map((s, i) => (
                <button
                  key={s.id}
                  type='button'
                  className={clsx(
                    'sc-nav__dot',
                    i === story.active && 'is-active'
                  )}
                  onClick={() => story.scrollToChapter(i)}
                  aria-label={copy.chapters[s.id]}
                  title={copy.chapters[s.id]}
                />
              ))}
            </span>
            <span className='sc-nav__label'>{copy.chapters[current.id]}</span>
            {navExtra}
            <span className='sc-lang' role='group' aria-label='Sprache'>
              {LANGS.map((l) => (
                <button
                  key={l}
                  type='button'
                  className={clsx('sc-lang__btn', l === lang && 'is-active')}
                  aria-pressed={l === lang}
                  onClick={() => onLang(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </span>
          </nav>
        </div>

        {sections.map(({ id, height, tone = 'dark', flow, Scene }, i) => (
          <section
            key={id}
            id={`sc-${id}`}
            ref={(el) => (story.chapterRefs.current[i] = el)}
            className={clsx(
              'sc-section',
              `sc-tone--${tone}`,
              flow && 'sc-flow'
            )}
            style={flow ? undefined : { height: `${height}vh` }}
            aria-label={copy.chapters[id]}
          >
            <div
              ref={(el) => (story.sceneRefs.current[i] = el)}
              className={flow ? 'sc-flow__inner' : 'sc-sticky'}
            >
              <SceneProvider index={i} subscribe={story.subscribe}>
                <Scene />
              </SceneProvider>
            </div>
          </section>
        ))}
      </div>
    </StoryProvider>
  )
}

Story.propTypes = {
  sections: sectionShape.isRequired,
  copy: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  onLang: PropTypes.func.isRequired,
  navExtra: PropTypes.node,
  theme: PropTypes.oneOf(['dark', 'light']),
  className: PropTypes.string
}
